<template>
  <div class="mw" :class="`mw--${viz}`">
    <div v-if="!data && loading" class="mw-skeleton"></div>
    <p v-else-if="error && !data" class="ov-empty">{{ $t('dash.overview.error') }}</p>
    <p v-else-if="!entries.length" class="ov-empty">{{ $t('dash.overview.widgets.no_data') }}</p>

    <!-- Tuiles : valeur + évolution -->
    <div v-else-if="viz === 'tile'" class="mw-tiles">
      <div v-for="entry in entries" :key="entry.key" class="mw-tile">
        <Tooltip :text="entry.help" :active="!!entry.help" position="top" class="mw-label-wrap">
          <span class="mw-label">{{ entry.label }}<i v-if="entry.help" class="material-icons mw-help">info_outline</i></span>
        </Tooltip>
        <Tooltip :text="entry.exact" :active="!!entry.exact" position="top">
          <span class="mw-value">{{ entry.display }}</span>
        </Tooltip>
        <span v-if="entry.trend" class="trend-badge" :class="entry.trend.cls">
          <i class="material-icons">{{ entry.trend.icon }}</i>{{ entry.trend.text }}
          <small>vs {{ entry.prevDisplay }}</small>
        </span>
      </div>
    </div>

    <!-- Courbe / barres par jour -->
    <div v-else-if="viz === 'line' || viz === 'bar'" class="mw-chart">
      <Graph v-if="chart.labels.length > 1" :id="`mw-${uid}`" :key="chartKey" :data="chart.data" :labels="chart.labels" height="100%" width="100%" />
      <p v-else class="ov-empty">{{ $t('dash.overview.widgets.no_series') }}</p>
    </div>

    <!-- Répartition par page, groupe de pages ou réseau -->
    <MiniDonut v-else-if="viz === 'donut'" :items="distribution" />

    <!-- Classement des pages, groupes ou réseaux -->
    <ol v-else-if="viz === 'ranking'" class="mw-ranking">
      <li v-for="(row, idx) in ranking" :key="row.id">
        <span class="mw-rank">{{ idx + 1 }}</span>
        <span class="mw-rank-name">{{ row.name }}</span>
        <span class="mw-rank-bar"><span :style="{ width: `${row.share}%` }"></span></span>
        <strong>{{ row.display }}</strong>
      </li>
    </ol>
  </div>
</template>

<script>
import moment from 'moment'
import Graph from '../../../reporting-web/Graph.vue'
import Tooltip from '../../../tooltip/tooltipInit.vue'
import MiniDonut from './MiniDonut.vue'
import { apiGet, cachedGet, rangeParams, compact, PALETTE } from './widgetFetch'
import { metricLabel, metricHelp } from './metricLabels'
import { groupPages } from '../pageGroups'

let uidCounter = 0

/**
 * Widget « Métrique » : toute métrique du reporting agrégée sur les pages du tableau de bord
 * options : keys (1 à 4), viz (tile | line | bar | donut | ranking), compare, network, groupBy (page | group | network), top
 */
export default {
  name: 'MetricWidget',

  components: { Graph, Tooltip, MiniDonut },

  props: {
    options: { type: Object, default: () => ({}) },
    pageIds: { type: Array, default: null },
    pages: { type: Array, default: () => [] },
    range: { type: Object, required: true },
    refreshToken: { type: Number, default: 0 }
  },

  data() {
    return { uid: ++uidCounter, data: null, loading: false, error: false }
  },

  computed: {
    keys() {
      return (this.options.keys && this.options.keys.length ? this.options.keys : ['impressions']).slice(0, 4)
    },

    viz() {
      return this.options.viz || 'tile'
    },

    path() {
      return `/reporting/metrics?${rangeParams(this.range, { pages: this.pageIds, keys: this.keys, network: this.options.network })}`
    },

    entries() {
      const metrics = (this.data && this.data.metrics) || {}
      return this.keys.filter(key => metrics[key]).map(key => {
        const m = metrics[key]
        const percent = m.kind === 'ratio'
        const compare = this.options.compare !== false && m.previous !== null && m.previous !== undefined
        return {
          key,
          metric: m,
          label: metricLabel(this, key, this.options.network),
          help: metricHelp(this, key, this.options.network),
          display: compact(m.total, percent),
          exact: !percent && Math.abs(m.total) >= 10000 ? Number(m.total).toLocaleString() : '',
          prevDisplay: compact(m.previous, percent),
          trend: compare ? this.trend(m.total, m.previous) : null
        }
      })
    },

    chartKey() {
      return `${this.path}-${this.viz}`
    },

    chart() {
      const days = new Set()
      const byDay = this.entries.map(entry => {
        const map = {}
        ;(entry.metric.series || []).forEach(d => {
          const day = moment(d.date * 1000).format('YYYY-MM-DD')
          map[day] = (map[day] || 0) + d.total
          days.add(day)
        })
        return map
      })
      const sorted = [...days].sort()
      return {
        labels: sorted.map(d => moment(d).format('DD/MM')),
        data: this.entries.map((entry, idx) => ({
          label: entry.label,
          data: sorted.map(d => byDay[idx][d] || 0),
          type: this.viz === 'bar' ? 'bar' : 'line',
          color: PALETTE[idx % PALETTE.length]
        }))
      }
    },

    // Première métrique ventilée par page, par groupe de pages du client ou par réseau, triée par valeur
    breakdown() {
      const entry = this.entries[0]
      if (!entry) return []
      const byPage = entry.metric.by_page || []
      let rows
      if (this.options.groupBy === 'network') {
        rows = Object.entries(entry.metric.by_network || {}).map(([network, value]) => ({ id: network, label: this.$t(`dash.overview.network.${network}`), value }))
      } else if (this.options.groupBy === 'group') {
        // Somme des pages du groupe ; taux : moyenne des pages (pas de dénominateur côté front)
        const values = new Map(byPage.map(row => [String(row.id), row.value]))
        const withData = this.pages.filter(p => values.has(String(p.id)))
        rows = groupPages(this.$store, withData, this.$t('dash.overview.groups.others')).map(group => {
          const list = group.pages.map(p => values.get(String(p.id)) || 0)
          const sum = list.reduce((s, v) => s + v, 0)
          return { id: group.key, label: group.name || this.$t('dash.pages.group.all_pages'), value: entry.metric.kind === 'ratio' ? sum / list.length : sum }
        })
      } else {
        rows = byPage.map(row => ({ id: row.id, label: this.pageName(row.id), value: row.value }))
      }
      return rows.sort((a, b) => b.value - a.value)
    },

    distribution() {
      return this.breakdown.map(({ label, value }) => ({ label, value }))
    },

    ranking() {
      const entry = this.entries[0]
      if (!entry) return []
      const rows = this.breakdown.slice(0, this.options.top || 5)
      const max = Math.max(1, ...rows.map(r => r.value))
      const percent = entry.metric.kind === 'ratio'
      return rows.map(r => ({ id: r.id, name: r.label, display: compact(r.value, percent), share: Math.round(r.value / max * 100) }))
    }
  },

  watch: {
    path() {
      this.load()
    },

    refreshToken() {
      this.load(true)
    }
  },

  created() {
    this.load()
  },

  methods: {
    async load(refresh = false) {
      const path = this.path
      const cached = cachedGet(path)
      if (cached) this.data = cached
      this.loading = true
      this.error = false
      try {
        const body = await apiGet(path, { refresh })
        if (path === this.path) this.data = body
      } catch (e) {
        if (path === this.path) this.error = true
      } finally {
        if (path === this.path) this.loading = false
      }
    },

    pageName(id) {
      const page = this.pages.find(p => String(p.id) === String(id))
      return (page && page.name) || id
    },

    trend(value, prev) {
      if (!prev) return null
      const delta = (value - prev) / Math.abs(prev) * 100
      const rounded = Math.round(delta * 10) / 10
      if (!rounded) return { cls: 'flat', icon: 'trending_flat', text: '0%' }
      return rounded > 0
        ? { cls: 'up', icon: 'trending_up', text: `+${rounded}%` }
        : { cls: 'down', icon: 'trending_down', text: `${rounded}%` }
    }
  }
}
</script>

<style scoped>
.mw {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.mw-skeleton {
  flex: 1;
  border-radius: 6px;
  background: #f1edf5;
}
.mw-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  align-content: start;
}
.mw-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.mw-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #555e75;
}
.mw-help {
  font-size: 13px;
  color: #9ba3b7;
}
.mw-value {
  font-size: 26px;
  font-weight: 700;
  color: #121826;
  line-height: 1.15;
}
.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 600;
}
.trend-badge .material-icons {
  font-size: 14px;
}
.trend-badge small {
  margin-left: 4px;
  font-weight: 500;
  color: #9ba3b7;
}
.trend-badge.up { background: #e6f9f0; color: #1a9e5e; }
.trend-badge.down { background: #fdf2f2; color: #d93025; }
.trend-badge.flat { background: #f1edf5; color: #555e75; }
.mw-chart {
  position: relative;
  flex: 1;
  min-height: 0;
}
.mw-ranking {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
}
.mw-ranking li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12.5px;
  color: #121826;
}
.mw-rank {
  width: 18px;
  font-weight: 700;
  color: #ab6e93;
  text-align: center;
}
.mw-rank-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mw-rank-bar {
  width: 30%;
  height: 6px;
  border-radius: 3px;
  background: #f1edf5;
  overflow: hidden;
}
.mw-rank-bar span {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #6b1f4d;
}
</style>
